// FrontEnd VectorUploader.jsx
import React, { useState, useRef, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";

const API_BASE = "/api/vsuploader";
const BATCH_SIZE = 5;            // cantidad de archivos por lote para mostrar progreso real
const MAX_SIZE_MB = 40;          // tamaño máximo por archivo

export default function VectorUploader() {
  const { username } = useContext(AuthContext);

  const [sessionId, setSessionId] = useState("");
  const [mode, setMode] = useState("zip");
  const [progress, setProgress] = useState(0);
  const [msg, setMsg] = useState("");

  // revisión
  const [renamed, setRenamed] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());

  // modal de aprobación
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveProgress, setApproveProgress] = useState(0);   // 0..100
  const [approveWorking, setApproveWorking] = useState(false);
  const [approveSummary, setApproveSummary] = useState(null);

  const fileInputZip = useRef(null);
  const fileInputFolder = useRef(null);

  const resetReview = () => {
    setRenamed([]); setCandidates([]); setSelected(new Set());
    setApproveSummary(null);
    setApproveProgress(0);
  };

  // ===== Helpers de validación de tamaño =====
  const overLimit = (sizeBytes) => sizeBytes > MAX_SIZE_MB * 1024 * 1024;

  const validateZip = (file) => {
    if (!file) return false;
    if (overLimit(file.size)) {
      alert(`El archivo supera el máximo permitido (${MAX_SIZE_MB} MB).`);
      return false;
    }
    return true;
  };

  const validateFolderFiles = (files) => {
    if (!files?.length) return false;
    const tooBig = Array.from(files).find((f) => overLimit(f.size));
    if (tooBig) {
      const sizeMB = (tooBig.size / (1024 * 1024)).toFixed(2);
      alert(`"${tooBig.name}" pesa ${sizeMB} MB y excede el máximo de ${MAX_SIZE_MB} MB.`);
      return false;
    }
    return true;
  };

  const axiosErrMsg = (err) =>
    `Error: ${err?.response?.status || ""} ${err?.response?.statusText || ""} ${
      err?.response?.data?.msg || err?.message || ""
    }`.trim();

  // ===== Upload ZIP =====
  const handleUploadZip = async (file) => {
    if (!file) return;
    if (!validateZip(file)) return;

    setMsg("Subiendo ZIP…");
    setProgress(0);

    const fd = new FormData();
    fd.append("file", file);

    try {
      const res = await axios.post(`${API_BASE}/upload-zip`, fd, {
        withCredentials: true,
        headers: username ? { "X-Username": username } : {},
        onUploadProgress: (e) =>
          setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });

      if (!res?.data?.sessionId) {
        setMsg("Error: el servidor no devolvió sessionId.");
        return;
      }

      setMsg(`ZIP subido. sessionId=${res.data.sessionId}. Archivos: ${res.data.filesUploaded}`);
      setSessionId(res.data.sessionId);
      resetReview();
    } catch (err) {
      console.error("upload-zip error", err);
      setMsg(axiosErrMsg(err));
    }
  };

  // ===== Upload Carpeta =====
  const handleUploadFolder = async (files) => {
    if (!files?.length) return;
    if (!validateFolderFiles(files)) return;

    setMsg("Subiendo carpeta…");
    setProgress(0);

    const fd = new FormData();
    const paths = [];
    Array.from(files).forEach((f) => {
      fd.append("files", f, f.name);
      paths.push(f.webkitRelativePath || f.name);
    });
    paths.forEach((p) => fd.append("paths", p));

    try {
      const res = await axios.post(`${API_BASE}/upload-folder`, fd, {
        withCredentials: true,
        headers: username ? { "X-Username": username } : {},
        onUploadProgress: (e) =>
          setProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });

      if (!res?.data?.sessionId) {
        setMsg("Error: el servidor no devolvió sessionId.");
        return;
      }

      setMsg(`Carpeta subida. sessionId=${res.data.sessionId}. Archivos: ${res.data.filesUploaded}`);
      setSessionId(res.data.sessionId);
      resetReview();
    } catch (err) {
      console.error("upload-folder error", err);
      setMsg(axiosErrMsg(err));
    }
  };

  // ===== Preprocess =====
  const handlePreprocess = async () => {
    if (!sessionId) { setMsg("Primero sube archivos para generar un sessionId."); return; }
    setMsg("Preprocesando (aplanar + normalizar + colisiones)...");
    try {
      const res = await axios.post(`${API_BASE}/preprocess`, { sessionId }, { withCredentials: true });
      setRenamed(res.data.renamed || []);
      const cands = res.data.candidates || [];
      setCandidates(cands);
      setSelected(new Set(cands));
      setMsg(`Listo. ${cands.length} candidatos válidos para subir.`);
      setApproveSummary(null);
    } catch (err) {
      console.error("preprocess error", err);
      setMsg(axiosErrMsg(err));
    }
  };

  const toggleOne = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === candidates.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(candidates));
    }
  };

  // ===== Cálculos de estado para habilitar/deshabilitar =====
  const isUploading = progress > 0 && progress < 100;
  const canApprove = selected.size > 0 && !isUploading;

  // ===== Aprobación con modal y progreso =====
  const openApproveModal = () => {
    if (!canApprove) return; // bloqueo defensivo
    setApproveSummary(null);
    setApproveProgress(0);
    setShowApproveModal(true);
  };

  const doApproveBatched = async () => {
    const keys = Array.from(selected);
    if (keys.length === 0) return;

    setApproveWorking(true);
    setApproveProgress(0);

    let total = keys.length;
    let done = 0;
    let ok = 0;
    let failed = 0;

    for (let i = 0; i < keys.length; i += BATCH_SIZE) {
      const batch = keys.slice(i, i + BATCH_SIZE);
      try {
        const res = await axios.post(`${API_BASE}/approve`, { sessionId, keys: batch }, { withCredentials: true });
        ok += res.data.summary?.ok || 0;
        failed += res.data.summary?.failed || 0;
      } catch (e) {
        failed += batch.length;
      }
      done += batch.length;
      setApproveProgress(Math.round((done * 100) / total));
    }

    try {
      const again = await axios.post(`${API_BASE}/preprocess`, { sessionId }, { withCredentials: true });
      const cands = again.data.candidates || [];
      setCandidates(cands);
      setSelected(new Set(cands));
      setRenamed(again.data.renamed || []);
    } catch (err) {
      console.error("refresh after approve error", err);
    }

    setApproveWorking(false);
    setApproveSummary({ total, ok, failed });
  };

  const closeApproveModal = () => {
    if (approveWorking) return;
    setShowApproveModal(false);
  };

  // ===== Cancelar sesión =====
  const handleCancelSession = async () => {
    if (!sessionId) { setMsg("No hay sessionId para cancelar."); return; }
    const sure = window.confirm("Esto borrará TODO lo subido en esta sesión en 'uploadsinprocesar'. ¿Deseas continuar?");
    if (!sure) return;

    try {
      const res = await axios.delete(`${API_BASE}/sessions/${sessionId}`, { withCredentials: true });
      setMsg(`Sesión cancelada. Eliminados en IN: ${res.data.deleted_in}.`);
      setSessionId("");
      resetReview();
    } catch (err) {
      console.error("cancel session error", err);
      setMsg(axiosErrMsg(err));
    }
  };

  const handleCancelSessionAlsoOk = async () => {
    if (!sessionId) { setMsg("No hay sessionId para cancelar."); return; }
    const sure = window.confirm("Esto borrará TODO lo subido (IN y también OK). ¿Realmente quieres borrar también lo ya movido a 'subidosavectorstore'?");
    if (!sure) return;

    try {
      const res = await axios.delete(`${API_BASE}/sessions/${sessionId}?alsoOk=true`, { withCredentials: true });
      setMsg(`Sesión cancelada por completo. IN: ${res.data.deleted_in}, OK: ${res.data.deleted_ok}`);
      setSessionId("");
      resetReview();
    } catch (err) {
      console.error("cancel all error", err);
      setMsg(axiosErrMsg(err));
    }
  };

  const handleRefreshStatus = async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API_BASE}/sessions/${sessionId}/status`, { withCredentials: true });
      setMsg(`Keys IN: ${res.data.in?.length || 0}, Keys OK: ${res.data.okFolder?.length || 0}`);
    } catch (err) {
      console.error("status error", err);
      setMsg(axiosErrMsg(err));
    }
  };

  return (
    <div className="p-4 w-full max-w-none">
      <h2 className="text-xl font-semibold mb-3">
        Uploader → S3 → Preprocesar → Revisar → Aprobar → Vector Store
      </h2>

      {/* Cabecera: usuario + session chip */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="text-sm text-gray-700">
          Usuario:{" "}
          <span className="px-2 py-1 bg-gray-100 border rounded">
            {username || "—"}
          </span>
        </div>
        <div className="text-sm text-gray-700">
          Session ID:{" "}
          <span className="px-2 py-1 bg-gray-100 border rounded font-mono">
            {sessionId || "— (se asigna al subir)"}
          </span>
        </div>
      </div>

      <div className="flex gap-6 items-center mb-3 flex-wrap">
        <label className="flex items-center gap-2">
          <input type="radio" checked={mode==="zip"} onChange={()=>setMode("zip")} />
          ZIP
        </label>
        <label className="flex items-center gap-2">
          <input type="radio" checked={mode==="folder"} onChange={()=>setMode("folder")} />
          Carpeta
        </label>
      </div>

      {mode === "zip" ? (
        <div className="mb-4">
          <input
            type="file"
            accept=".zip"
            ref={fileInputZip}
            onChange={(e)=>handleUploadZip(e.target.files?.[0])}
          />
          <p className="text-xs text-gray-500 mt-1">
            ⚠️ Tamaño máximo permitido por archivo: {MAX_SIZE_MB} MB.
          </p>
        </div>
      ) : (
        <div className="mb-4">
          <input
            type="file"
            ref={fileInputFolder}
            webkitdirectory="true"
            mozdirectory="true"
            directory="true"
            onChange={(e)=>handleUploadFolder(e.target.files)}
          />
          <p className="text-xs text-gray-500 mt-1">
            Selecciona una carpeta para preservar la estructura. ⚠️ Máx por archivo: {MAX_SIZE_MB} MB.
          </p>
        </div>
      )}

      <div className="mb-4">
        <div className="h-2 bg-gray-200 rounded">
          <div className="h-2 bg-blue-500 rounded" style={{ width: `${progress}%`, transition: "width .2s" }}/>
        </div>
        <p className="text-sm mt-2 break-words">{msg}</p>
      </div>

{/* Botonera responsive a lo ancho */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-6">
  <button
    className={`px-4 py-2 rounded w-full ${
      isUploading ? "bg-gray-300 text-gray-600 cursor-not-allowed" : "bg-indigo-600 text-white"
    }`}
    onClick={handlePreprocess}
    disabled={isUploading}
    title={isUploading ? "Espera a que termine la subida" : "Analizar archivos"}
  >
    1) Preprocesar y ver nombres
  </button>

  <button
    className={`px-4 py-2 rounded w-full ${
      canApprove ? "bg-blue-600 text-white" : "bg-gray-300 text-gray-600 cursor-not-allowed"
    }`}
    onClick={openApproveModal}
    disabled={!canApprove}
    aria-disabled={!canApprove}
    title={!canApprove ? (isUploading ? "Esperando a que termine la subida" : "Selecciona al menos un archivo") : "Aprobar y subir"}
  >
    2) Aprobar seleccionados y subir
  </button>

  <button className="px-4 py-2 rounded bg-gray-200 w-full" onClick={handleRefreshStatus}>
    Ver estado sesión
  </button>

  <button className="px-4 py-2 rounded bg-rose-600 text-white w-full" onClick={handleCancelSession}>
    Cancelar sesión (solo IN)
  </button>

  <button className="px-4 py-2 rounded bg-rose-700 text-white w-full" onClick={handleCancelSessionAlsoOk}>
    Cancelar TODO (IN + OK)
  </button>
</div>


      {/* Tabla de cambios (from → to) */}
      {renamed.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold mb-2">Cambios realizados en preproceso</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-2 border">Tipo</th>
                  <th className="p-2 border">Desde</th>
                  <th className="p-2 border">Hacia</th>
                  <th className="p-2 border">Curso</th>
                  <th className="p-2 border">Padre</th>
                  <th className="p-2 border">Notas</th>
                </tr>
              </thead>
              <tbody>
                {renamed.map((r, i)=>(
                  <tr key={i} className="odd:bg-white even:bg-gray-50">
                    <td className="p-2 border">
                      {r.skipped ? "SKIPPED" : (r.renamed ? "RENAMED" : "UNCHANGED")}
                    </td>
                    <td className="p-2 border font-mono break-all">{r.from}</td>
                    <td className="p-2 border font-mono break-all">{r.to}</td>
                    <td className="p-2 border">{r.courseCode || "-"}</td>
                    <td className="p-2 border">{r.parent || "-"}</td>
                    <td className="p-2 border">{r.reason || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            * “SKIPPED” = extensión no permitida (solo PDF/DOC/DOCX se podrán subir).
          </p>
        </div>
      )}

      {/* Tabla de candidatos con selección */}
      {candidates.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">
              Archivos candidatos (PDF/DOC/DOCX) — {candidates.length}
            </h3>
            <button className="px-3 py-1 rounded border" onClick={toggleAll}>
              {selected.size === candidates.length ? "Deseleccionar todo" : "Seleccionar todo"}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-2 border">✓</th>
                  <th className="p-2 border">S3 Key (IN)</th>
                  <th className="p-2 border">Nombre</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((k)=>(
                  <tr key={k} className="odd:bg-white even:bg-gray-50">
                    <td className="p-2 border text-center">
                      <input type="checkbox" checked={selected.has(k)} onChange={()=>toggleOne(k)} />
                    </td>
                    <td className="p-2 border font-mono break-all">{k}</td>
                    <td className="p-2 border break-all">{k.split("/").pop()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Se subirán únicamente los archivos seleccionados.
          </p>
        </div>
      )}

      {/* Modal de aprobación */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-2">Confirmar subida al Vector Store</h3>
            <p className="text-sm text-gray-600 mb-4">
              Se enviarán <strong>{selected.size}</strong> archivo(s) en lotes de {BATCH_SIZE}.
            </p>

            <div className="mb-4">
              <div className="h-2 bg-gray-200 rounded">
                <div className="h-2 bg-blue-600 rounded" style={{ width: `${approveProgress}%`, transition: "width .2s" }} />
              </div>
              <p className="text-xs mt-2">Progreso: {approveProgress}%</p>
            </div>

            {approveSummary && (
              <div className="text-sm mb-3">
                <p>Terminó la subida: OK: {approveSummary.ok} | Fallidos: {approveSummary.failed} | Total: {approveSummary.total}</p>
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                className="px-4 py-2 rounded border"
                onClick={closeApproveModal}
                disabled={approveWorking}
              >
                {approveSummary ? "Cerrar" : "Cancelar"}
              </button>
              {!approveSummary && (
                <button
                  className="px-4 py-2 rounded bg-blue-600 text-white"
                  onClick={doApproveBatched}
                  disabled={approveWorking}
                >
                  {approveWorking ? "Subiendo..." : "Confirmar y subir"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
